import { useState, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

export const useSidebar = (initialOpen: boolean = true) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  // Different state for different breakpoints
  const [desktopOpen, setDesktopOpen] = useState(initialOpen);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Auto-close mobile drawer when switching to desktop
  useEffect(() => {
    if (!isMobile && mobileOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs drawer state with the media-query breakpoint (external system); closing in render would drop the open desktop state
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  // Auto-collapse sidebar on tablet for more space — but ONLY when first entering the tablet
  // breakpoint, never on subsequent renders. Keeping `desktopOpen` in the dependency list (or
  // re-checking it every render) made this effect re-fire the instant a user manually expanded
  // the sidebar on a tablet-width window: toggle → desktopOpen=true → effect → setDesktopOpen(false),
  // so the menu visibly popped open and snapped shut. Reacting only to the false→true `isTablet`
  // transition lets the initial/resize collapse happen once while leaving manual toggles alone.
  const wasTablet = useRef(false);
  useEffect(() => {
    if (isTablet && !wasTablet.current) {
      setDesktopOpen(false);
    }
    wasTablet.current = isTablet;
  }, [isTablet]);

  const toggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen(prev => !prev);
    } else {
      setDesktopOpen(prev => !prev);
    }
  }, [isMobile]);

  const open = useCallback(() => {
    if (isMobile) {
      setMobileOpen(true);
    } else {
      setDesktopOpen(true);
    }
  }, [isMobile]);

  const close = useCallback(() => {
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setDesktopOpen(false);
    }
  }, [isMobile]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Current state based on device
  const isOpen = isMobile ? mobileOpen : desktopOpen;
  
  // Sidebar width calculations
  const sidebarWidth = {
    expanded: 280,
    collapsed: 70,
    mobile: 280
  };

  const currentWidth = isMobile 
    ? sidebarWidth.mobile 
    : isOpen 
      ? sidebarWidth.expanded 
      : sidebarWidth.collapsed;

  return {
    isOpen,
    toggle,
    open,
    close,
    closeMobile,
    isMobile,
    isTablet,
    isDesktop,
    mobileOpen,
    desktopOpen,
    sidebarWidth,
    currentWidth,
  };
};
